// Requests — the pure rules behind the Requests page (no fetch, no DOM), so
// test/requests.test.js can run them under plain node.
//
// A request is an INTERNAL ask: a staffer mentions @genius in Slack and
// squish-crm-backend turns the mention into a card. Nothing here is a customer
// ticket, and none of it comes from Salesforce.
//
// Every string on a card was typed into Slack by somebody. It is rendered as
// text and never interpreted; the one field that becomes an href goes through
// slackPermalink() first.

export const OUTCOMES = [
  { value: 'done',      label: 'Done' },
  { value: 'wont_do',   label: "Won't do" },
  { value: 'duplicate', label: 'Duplicate' },
];

export function outcomeLabel(outcome) {
  return OUTCOMES.find(o => o.value === outcome)?.label || 'Closed';
}

// The closing note's ceiling. The server refuses a longer one; this is the
// number the counter under the box counts against.
export const CLOSE_NOTE_MAX = 1500;

const sameEmail = (a, b) => {
  const x = typeof a === 'string' ? a.trim().toLowerCase() : '';
  const y = typeof b === 'string' ? b.trim().toLowerCase() : '';
  return x !== '' && x === y;
};

// Is this card the caller's? Email is the identity, compared without case:
// Cloudflare Access and Salesforce do not agree on how to capitalise one.
// A card with no owner, or a caller we could not identify, is nobody's.
export function isMine(request, me) {
  return sameEmail(request?.owner?.email, me?.email);
}

const byCreated = (a, b) => (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0);
const byClosedDesc = (a, b) => (Number(b.closedAt) || 0) - (Number(a.closedAt) || 0);

// The four lists the page shows. Open and in-progress cards run oldest first —
// the one that has waited longest is at the top, and a poll never reshuffles
// rows under the cursor. Closed runs newest first.
//
// Only 'open' is unassigned and only 'closed' is closed. Anything else —
// 'claimed', or a status this build has not heard of — is somebody's work in
// progress: a card dropped from every list is a request nobody will ever see.
export function groupRequests(requests, me) {
  const unassigned = [], mine = [], others = [], closed = [];
  for (const r of Array.isArray(requests) ? requests : []) {
    if (!r || typeof r !== 'object') continue;
    if (r.status === 'closed') closed.push(r);
    else if (r.status === 'open') unassigned.push(r);
    else if (isMine(r, me)) mine.push(r);
    else others.push(r);
  }
  unassigned.sort(byCreated);
  mine.sort(byCreated);
  others.sort(byCreated);
  closed.sort(byClosedDesc);
  return { unassigned, mine, others, closed };
}

// Put the card a mutation returned into the list in place of the copy it
// replaces. A card the list has not seen yet is added.
export function upsertRequest(requests, request) {
  const list = Array.isArray(requests) ? requests : [];
  if (!request?.id) return list;
  return list.some(r => r?.id === request.id)
    ? list.map(r => (r?.id === request.id ? request : r))
    : [...list, request];
}

// A "Take over?" offer ({ id, owner, raced }) read against a freshly loaded
// list. It is only good while someone else still has the card: released,
// closed, already the caller's, or gone from the list, and it is dropped. If
// the card changed hands meanwhile, the offer names whoever has it NOW — that
// is who the forced claim would take it from.
export function standingOffer(offer, requests, me) {
  if (!offer?.id) return null;
  const card = (Array.isArray(requests) ? requests : []).find(r => r?.id === offer.id);
  if (!card || card.status === 'open' || card.status === 'closed' || isMine(card, me)) return null;
  return { ...offer, owner: card.owner || offer.owner || null };
}

// The Slack link, or null. A permalink is only ever rendered as a link when it
// is https and its host is a Slack workspace (something.slack.com). Judged on
// the PARSED hostname, not on the string: "https://x.slack.com.evil.example",
// "https://evil.example/.slack.com" and "https://team.slack.com@evil.example"
// all contain the right letters and all go somewhere else.
export function slackPermalink(permalink) {
  if (typeof permalink !== 'string' || !permalink.startsWith('https://')) return null;
  let url;
  try { url = new URL(permalink); } catch { return null; }
  if (url.protocol !== 'https:') return null;
  if (!url.hostname.toLowerCase().endsWith('.slack.com')) return null;
  // What was judged is what gets rendered.
  return url.href;
}

// "#ops" when Slack told us the channel's name, else the bare channel id.
export function channelLabel(slack) {
  const name = typeof slack?.channelName === 'string' ? slack.channelName.trim().replace(/^#+/, '') : '';
  if (name) return `#${name}`;
  return typeof slack?.channel === 'string' && slack.channel.trim() ? slack.channel.trim() : null;
}

const MIN = 60_000, HOUR = 60 * MIN, DAY = 24 * HOUR;

// "just now", "12m ago", "3h ago", "5d ago", "2mo ago", "1y ago". A missing
// or unreadable time is "—", never "just now": a card that cannot say how old
// it is must not look like the newest one. A time slightly in the future (the
// server's clock against this one) is just now.
export function relativeAge(at, now = Date.now()) {
  if (at === null || at === undefined || at === '') return '—';
  const t = Number(at);
  if (!Number.isFinite(t) || t <= 0) return '—';
  const ms = now - t;
  if (ms < MIN) return 'just now';
  if (ms < HOUR) return `${Math.floor(ms / MIN)}m ago`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)}h ago`;
  const days = Math.floor(ms / DAY);
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

// The images on a card the page may ask for: a Slack file id, one of the four
// raster types backend keeps. Anything else in the field is dropped rather
// than turned into a request for something that is not there.
const IMAGE_ID_RE = /^F[A-Z0-9]{2,20}$/;
const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
export function requestImages(request) {
  const files = Array.isArray(request?.files) ? request.files : [];
  return files.filter(f => f && IMAGE_ID_RE.test(String(f.id || '')) && IMAGE_TYPES.has(String(f.mimetype || '').toLowerCase()))
    .map(f => ({ id: f.id, name: typeof f.name === 'string' && f.name.trim() ? f.name.trim() : 'image', from: f.from === 'thread' ? 'thread' : 'ask' }));
}

// Has an unassigned card waited too long? Two days with nobody on it is the
// point where the person who asked starts wondering whether anyone saw it.
// Only open cards go stale: one with an owner is being worked, and a closed
// one is done. An unreadable time is not stale — no claim without a reading.
export const STALE_AFTER_MS = 2 * DAY;
export function isStale(request, now = Date.now()) {
  if (request?.status !== 'open') return false;
  const t = Number(request.createdAt);
  return Number.isFinite(t) && t > 0 && now - t > STALE_AFTER_MS;
}

// The one or two letters on a person's chip: first and last word of the name.
// An email (what a nameless owner falls back to) gives its first letter; no
// name at all gives "?" rather than an empty circle.
export function initials(name) {
  const parts = (typeof name === 'string' ? name : '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
}

// A thread message's time as ms epoch, or null. Slack stamps messages in
// seconds with a fractional counter ("1760000000.000200"); an ISO string is
// read too, in case the backend normalises it.
export function threadTime(ts) {
  if (typeof ts === 'number') return Number.isFinite(ts) && ts > 0 ? Math.round(ts < 1e11 ? ts * 1000 : ts) : null;
  if (typeof ts !== 'string' || !ts.trim()) return null;
  const s = ts.trim();
  if (/^\d{9,10}(\.\d+)?$/.test(s)) return Math.round(parseFloat(s) * 1000);
  if (/^\d{13}$/.test(s)) return Number(s);
  const parsed = Date.parse(s);
  return Number.isFinite(parsed) ? parsed : null;
}

// What is wrong with a closing note, or '' when it can be sent. Counted the
// way it is sent: trimmed.
export function closeNoteProblem(note) {
  const text = typeof note === 'string' ? note.trim() : '';
  if (!text) return 'A closing note is required: it is what the poster is told.';
  if (text.length > CLOSE_NOTE_MAX) return `Too long: ${text.length.toLocaleString('en-US')} of ${CLOSE_NOTE_MAX.toLocaleString('en-US')} characters.`;
  return '';
}

// What Slack actually got when a card was closed, as lines to show under the
// resolution: [{ tone: 'ok' | 'warn', text }].
//
// Only an explicit answer is good news. A close whose delivery was never
// reported, or reported as anything else, reads as not delivered — the closer
// should go and look rather than assume the poster was told. The same goes
// for WHAT was delivered: "the note" is only said when the server said the
// note was not withheld.
export function deliveryLines(resolution, posterName) {
  const slack = resolution?.slack;
  if (!slack || typeof slack !== 'object') {
    return [{ tone: 'warn', text: 'Slack delivery was not reported for this close. Check the thread before assuming the poster was told.' }];
  }
  const who = (typeof posterName === 'string' && posterName.trim()) || 'the poster';
  const withheld = slack.noteWithheld === true;
  const what = withheld ? 'A link' : slack.noteWithheld === false ? 'The note' : 'A message';
  const lines = [];
  if (withheld) {
    lines.push({
      tone: 'warn',
      text: 'The closing note was NOT sent to Slack, because it looked like it contained an email address, an amount or a phone number. Slack only got a link.',
    });
  } else if (slack.noteWithheld !== false) {
    lines.push({ tone: 'warn', text: 'Slack did not say whether the note itself went, or only a link to it.' });
  }
  lines.push(slack.thread === true
    ? { tone: 'ok', text: `${what} was posted to the Slack thread.` }
    : { tone: 'warn', text: 'Nothing was posted to the Slack thread.' });
  lines.push(slack.dm === true
    ? { tone: 'ok', text: `${what} was DMed to ${who}.` }
    : { tone: 'warn', text: `No DM was sent to ${who}.` });
  return lines;
}
